import React from 'react';
import PropTypes from 'prop-types';
import {
  useHelmet, TitleTag, MetaTag, LinkTag, ScriptTag
} from '@thd-nucleus/thd-helmet';
import {
  params, string, json, useDataModel
} from '@thd-nucleus/data-sources';

import { EMT_PAGE_NAMES } from '../constants';
import { getLocalCityStructuredData } from './structured-data';
import { getCurrentPromoImageSrc, getPreloadImageTag } from '../helpers/pps-helpers';

const getTitle = ({ city, state }) => {
  if (!city || !state) return 'The Home Depot';

  return `Find Everything You Need at The Home Depot - ${city}, ${state}`;
};

const getMetaDescription = ({ city, state }) => {
  if (!city || !state) return 'The Home Depot';

  return 'Looking for the local Home Depot in your city? '
    + `Find everything you need in one place at The Home Depot in ${city}, ${state}.`;
};

const getCanonicalURL = ({ city, state, nvalue }) => {
  const formattedCity = city.trim().split(' ').join('-');

  return `https://www.homedepot.com/l/${state}/${formattedCity}/${nvalue}`;
};

const getMetadataValues = (data) => {
  const { emtData, city, state, nvalue } = data;
  const emtContentMetadata = emtData?.emtContent?.content?.metadata || {};

  const title = emtContentMetadata?.title || getTitle({ city, state });
  const canonicalURL = getCanonicalURL({ city, state, nvalue });
  const metaDescription = emtContentMetadata?.metaDescription || getMetaDescription({ city, state });
  const ogTitle = emtContentMetadata?.ogTitle || `Shop at The Home Depot - ${city}, ${state}`;
  const ogDescription = emtContentMetadata?.ogDescription || getMetaDescription({ city, state });
  const ogType = emtContentMetadata?.ogType || 'lcp-homepage';
  const ogImage = emtContentMetadata?.ogImage;
  const schema = emtContentMetadata?.schema;

  return {
    canonicalURL,
    title,
    metaDescription,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    schema,
  };
};

const localCityHandler = ({ props }) => {
  const tags = [];
  const {
    title,
    canonicalURL,
    metaDescription,
    ogDescription,
    ogTitle,
    ogType,
    ogImage,
    schema,
  } = getMetadataValues(props);

  tags.push(new TitleTag(title));
  tags.push(new MetaTag({ name: 'description', content: metaDescription, id: 'prop-description' }));
  tags.push(new LinkTag({ rel: 'canonical', href: canonicalURL, id: 'canonical' }));
  tags.push(new MetaTag({ property: 'og:title', content: ogTitle, id: 'og-title' }));
  tags.push(new MetaTag({ property: 'og:url', content: canonicalURL, id: 'og-url' }));
  tags.push(new MetaTag({ property: 'og:type', content: ogType, id: 'og-type' }));
  tags.push(new MetaTag({ property: 'og:description', content: ogDescription, id: 'og-description' }));
  if (ogImage) tags.push(new MetaTag({ property: 'og:image', content: ogImage, id: 'og-image' }));

  // Check for an active promotion and preload the image for the banner
  const promotionImageUrl = getCurrentPromoImageSrc(
    props?.emtData?.emtContent?.content?.storeFinderPromotions,
    EMT_PAGE_NAMES.LOCAL_CITY
  );
  if (promotionImageUrl) {
    tags.push(getPreloadImageTag(promotionImageUrl));
  }

  if (schema) {
    tags.push(new ScriptTag({ content: schema, id: 'localCityStructuredData' }));
  } else {
    const breadcrumbSchema = getLocalCityStructuredData('BreadcrumbList', props);
    tags.push(new ScriptTag({ content: breadcrumbSchema, id: 'localCityStructuredDataBreadcrumb' }));
  }

  return tags;
};

const LocalCityMetadata = ({ city, state, nvalue }) => {
  const emtContentOptions = {
    variables: {
      contentId: nvalue,
      pageName: EMT_PAGE_NAMES.LOCAL_CITY
    }
  };

  const { data: emtData, loading: emtLoading, error: emtError } = useDataModel('emtContent', emtContentOptions);

  const props = { city, state, nvalue, emtData };

  useHelmet('localCityMetadata', { props }, localCityHandler, [nvalue]);

  return null;
};

LocalCityMetadata.displayName = 'LocalCityMetadata';

LocalCityMetadata.dataModel = {
  emtContent: params({
    contentId: string().isRequired(),
    pageName: string().isRequired()
  }).shape({
    content: json()
  }),
};

LocalCityMetadata.propTypes = {
  city: PropTypes.string,
  state: PropTypes.string,
  nvalue: PropTypes.string,
};

LocalCityMetadata.defaultProps = {
  city: '',
  state: '',
  nvalue: '',
};

export { LocalCityMetadata };